import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Loader } from '../components/common/Loader';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getCart, removeFromCart, updateCartItemQuantity } from '../services/cartService';
import { Cart as CartType } from '../types';
import { motion } from 'framer-motion';

const Cart: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCart();
  }, [user]);

  const loadCart = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const data = await getCart(user.id);
      setCart(data);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (artworkId: string) => {
    if (!user) return;
    
    try {
      await removeFromCart(user.id, artworkId);
      await loadCart();
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleUpdateQuantity = async (artworkId: string, newQuantity: number) => {
    if (!user || newQuantity < 1) return;
    
    try {
      await updateCartItemQuantity(user.id, artworkId, newQuantity);
      await loadCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const calculateSubtotal = () => {
    if (!cart) return 0;
    return cart.items.reduce((sum, item) => sum + (item.artwork.price * item.quantity), 0);
  };

  const subtotal = calculateSubtotal();
  const shipping = subtotal > 0 ? 180 : 0;
  const tax = Math.round(subtotal * 0.01);
  const total = subtotal + shipping + tax;

  const handleProceedToCheckout = () => {
    if (!cart || cart.items.length === 0) return;
    
    navigate('/checkout', {
      state: {
        items: cart.items,
        subtotal,
        shipping,
        tax,
        total,
      }
    });
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent mb-6"
      >
        Shopping Cart
      </motion.h1>

      {(!cart || cart.items.length === 0) ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="w-32 h-32 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag size={64} className="text-primary" />
          </div>
          <p className="text-gray-600 mb-6 text-lg">Your cart is empty</p>
          <Button onClick={() => navigate('/explore')}>
            Browse Artworks
          </Button>
        </motion.div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {cart.items.map((item, index) => (
              <motion.div
                key={item.artworkId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-md flex gap-4 hover:shadow-lg transition-all"
              >
                <img
                  src={item.artwork.imageUrl}
                  alt={item.artwork.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">{item.artwork.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">by {item.artwork.artistName}</p>
                  <p className="text-lg font-bold text-primary">₹{item.artwork.price.toLocaleString()}</p>
                  
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex items-center gap-2 border-2 border-gray-300 rounded-lg">
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleUpdateQuantity(item.artworkId, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 rounded-l-lg"
                      >
                        <Minus size={16} />
                      </motion.button>
                      <span className="px-3 font-semibold">{item.quantity}</span>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleUpdateQuantity(item.artworkId, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 rounded-r-lg"
                      >
                        <Plus size={16} />
                      </motion.button>
                    </div>
                    
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleRemoveItem(item.artworkId)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={20} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Payment Details */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 shadow-md mb-6"
          >
            <h3 className="font-bold text-gray-800 mb-4 text-lg">Order Summary</h3>
            
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between">
                <span>Subtotal ({cart.items.length} items)</span>
                <span className="font-semibold">₹{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span className="font-semibold">₹{shipping}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax (GST)</span>
                <span className="font-semibold">₹{tax}</span>
              </div>
              <div className="border-t-2 border-gray-300 pt-3 flex justify-between text-lg font-bold">
                <span>Total Amount</span>
                <span className="text-primary">₹{total.toLocaleString()}</span>
              </div>
            </div>
          </motion.div>

          {/* Checkout Button */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Button 
              onClick={handleProceedToCheckout}
              fullWidth 
              className="shadow-xl py-4 text-lg flex items-center justify-center gap-2"
            >
              Proceed to Checkout
              <ArrowRight size={24} />
            </Button>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default Cart;